import React, {
  useEffect,
  useMemo,
  useState
} from "react";

import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Switch,
  TextField,
  Typography
} from "@material-ui/core";

import CloseIcon from "@material-ui/icons/Close";
import CreateIcon from "@material-ui/icons/Create";
import MailOutlineIcon from "@material-ui/icons/MailOutline";

import {
  toast
} from "react-toastify";

import api from "../../../services/api";
import toastError from "../../../errors/toastError";


const blankSignature = {
  displayName: "",
  jobTitle: "",
  phone: "",
  companyName: "",
  website: "",

  // KORAX EMAIL SIGNATURE LOGO FRONTEND V1
  logoData: "",
  logoContentType: "",
  logoFileName: "",

  enabled: true
};


const normalizeWebsite =
  value => {

    const clean =
      String(
        value || ""
      ).trim();

    if (!clean) {
      return "";
    }

    return clean
      .replace(
        /^https?:\/\//i,
        ""
      )
      .replace(
        /\/$/,
        ""
      );
  };


const EmailSignatureManager = ({
  companyId,
  mailboxes
}) => {

  const [
    open,
    setOpen
  ] = useState(false);

  const [
    loading,
    setLoading
  ] = useState(false);

  const [
    saving,
    setSaving
  ] = useState(false);

  const [
    rows,
    setRows
  ] = useState([]);

  const [
    signatures,
    setSignatures
  ] = useState([]);

  const [
    selectedMailboxId,
    setSelectedMailboxId
  ] = useState("");

  const [
    form,
    setForm
  ] = useState(
    blankSignature
  );


  const hasMailbox =
    Array.isArray(
      mailboxes
    ) &&
    mailboxes.length > 0;


  const selectedMailbox =
    useMemo(
      () =>
        rows.find(
          item =>
            Number(
              item.id
            ) ===
            Number(
              selectedMailboxId
            )
        ) || null,
      [
        rows,
        selectedMailboxId
      ]
    );


  const loadForm = (
    mailboxId,
    signatureRows
  ) => {

    const found =
      signatureRows.find(
        item =>
          Number(
            item.mailboxId
          ) ===
          Number(
            mailboxId
          )
      );


    setForm(
      found
        ? {
            displayName:
              found.displayName ||
              "",

            jobTitle:
              found.jobTitle ||
              "",

            phone:
              found.phone ||
              "",

            companyName:
              found.companyName ||
              "",

            website:
              found.website ||
              "",

            logoData:
              found.logoData ||
              "",

            logoContentType:
              found.logoContentType ||
              "",

            logoFileName:
              found.logoFileName ||
              "",

            enabled:
              found.enabled !==
              false
          }
        : {
            ...blankSignature
          }
    );
  };


  const load =
    async () => {

      if (!companyId) {
        return;
      }

      setLoading(true);

      try {

        const {
          data
        } =
          await api.get(
            "/email-beta/signature",
            {
              params: {
                companyId
              }
            }
          );


        const mailboxRows =
          Array.isArray(
            data?.mailboxes
          )
            ? data.mailboxes
            : [];


        const signatureRows =
          Array.isArray(
            data?.signatures
          )
            ? data.signatures
            : [];


        setRows(
          mailboxRows
        );

        setSignatures(
          signatureRows
        );


        const firstId =
          mailboxRows[0]?.id ||
          "";


        setSelectedMailboxId(
          firstId
        );


        if (firstId) {

          loadForm(
            firstId,
            signatureRows
          );

        } else {

          setForm({
            ...blankSignature
          });
        }

      } catch (err) {

        toastError(err);

      } finally {

        setLoading(false);
      }
    };


  const handleOpen =
    async () => {

      setOpen(true);

      await load();
    };


  const handleMailboxChange =
    event => {

      const mailboxId =
        event.target.value;


      setSelectedMailboxId(
        mailboxId
      );


      loadForm(
        mailboxId,
        signatures
      );
    };


  const updateField =
    field =>
      event => {

        setForm(
          previous => ({
            ...previous,

            [field]:
              event.target.value
          })
        );
      };


  const handleLogoChange =
    event => {

      const file =
        event.target.files?.[0];

      event.target.value =
        "";

      if (!file) {
        return;
      }


      const allowedTypes = [
        "image/png",
        "image/jpeg",
        "image/webp"
      ];


      if (
        !allowedTypes.includes(
          file.type
        )
      ) {

        toast.error(
          "Use uma imagem PNG, JPG ou WebP."
        );

        return;
      }


      if (
        file.size >
        1024 * 1024
      ) {

        toast.error(
          "A logo deve ter no máximo 1 MB."
        );

        return;
      }


      const reader =
        new FileReader();


      reader.onload =
        () => {

          const result =
            String(
              reader.result ||
              ""
            );


          const commaIndex =
            result.indexOf(",");


          if (
            commaIndex === -1
          ) {

            toast.error(
              "Não foi possível carregar esta imagem."
            );

            return;
          }


          const base64 =
            result.slice(
              commaIndex + 1
            );


          setForm(
            previous => ({
              ...previous,

              logoData:
                base64,

              logoContentType:
                file.type,

              logoFileName:
                file.name
            })
          );
        };


      reader.onerror =
        () => {

          toast.error(
            "Não foi possível carregar esta imagem."
          );
        };


      reader.readAsDataURL(
        file
      );
    };


  const handleRemoveLogo =
    () => {

      setForm(
        previous => ({
          ...previous,

          logoData:
            "",

          logoContentType:
            "",

          logoFileName:
            ""
        })
      );
    };


  const handleSave =
    async () => {

      if (
        !companyId ||
        !selectedMailboxId
      ) {
        return;
      }

      setSaving(true);

      try {

        const {
          data
        } =
          await api.put(
            `/email-beta/signature/${selectedMailboxId}`,
            {
              companyId,

              displayName:
                form.displayName,

              jobTitle:
                form.jobTitle,

              phone:
                form.phone,

              companyName:
                form.companyName,

              website:
                form.website,

              logoData:
                form.logoData,

              logoContentType:
                form.logoContentType,

              logoFileName:
                form.logoFileName,

              enabled:
                form.enabled
            }
          );


        const saved =
          data?.signature;


        setSignatures(
          previous => {

            const without =
              previous.filter(
                item =>
                  Number(
                    item.mailboxId
                  ) !==
                  Number(
                    selectedMailboxId
                  )
              );


            return saved
              ? [
                  ...without,
                  saved
                ]
              : previous;
          }
        );


        toast.success(
          "Assinatura salva com sucesso."
        );

      } catch (err) {

        toastError(err);

      } finally {

        setSaving(false);
      }
    };


  useEffect(
    () => {

      if (!open) {
        return;
      }

      load();

      // eslint-disable-next-line
    },
    [
      companyId
    ]
  );


  return (
    <>
      <Button
        variant="outlined"
        startIcon={
          <CreateIcon />
        }
        onClick={
          handleOpen
        }
        disabled={
          !companyId ||
          !hasMailbox
        }
        style={{
          height: 40,
          borderRadius: 10,
          borderColor: "#D0D5DD",
          background: "#FFFFFF",
          color: "#344054",
          textTransform: "none",
          fontSize: 11,
          fontWeight: 750
        }}
      >
        Minha assinatura
      </Button>


      <Dialog
        open={open}
        onClose={
          saving
            ? undefined
            : () =>
                setOpen(false)
        }
        fullWidth
        maxWidth="md"
        PaperProps={{
          style: {
            maxWidth: 900,
            borderRadius: 18,
            overflow: "hidden"
          }
        }}
      >
        <DialogTitle
          disableTypography
          style={{
            padding: 0
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            style={{
              padding:
                "20px 24px",
              borderBottom:
                "1px solid #EAECF0"
            }}
          >
            <Box>
              <Typography
                style={{
                  color: "#101828",
                  fontSize: 19,
                  fontWeight: 900,
                  letterSpacing:
                    "-.02em"
                }}
              >
                Minha assinatura
              </Typography>

              <Typography
                style={{
                  marginTop: 4,
                  color: "#667085",
                  fontSize: 11
                }}
              >
                Configure uma assinatura profissional para cada caixa de e-mail.
              </Typography>
            </Box>


            <IconButton
              size="small"
              disabled={
                saving
              }
              onClick={() =>
                setOpen(false)
              }
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>


        <DialogContent
          style={{
            padding: 0
          }}
        >
          {loading ? (

            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              style={{
                minHeight: 460
              }}
            >
              <CircularProgress
                size={28}
              />
            </Box>

          ) : rows.length ===
            0 ? (

            <Box
              style={{
                padding: 42,
                textAlign: "center"
              }}
            >
              <MailOutlineIcon
                style={{
                  color: "#98A2B3",
                  fontSize: 38
                }}
              />

              <Typography
                style={{
                  marginTop: 10,
                  color: "#344054",
                  fontSize: 13,
                  fontWeight: 800
                }}
              >
                Nenhuma caixa disponível para envio
              </Typography>
            </Box>

          ) : (

            <Box
              display="grid"
              style={{
                gridTemplateColumns:
                  "minmax(0, 1fr) minmax(330px, .95fr)",
                minHeight: 500
              }}
            >

              {/* FORMULARIO */}

              <Box
                style={{
                  padding: 24,
                  borderRight:
                    "1px solid #EAECF0"
                }}
              >
                <TextField
                  select
                  fullWidth
                  variant="outlined"
                  size="small"
                  label="Caixa de e-mail"
                  value={
                    selectedMailboxId
                  }
                  onChange={
                    handleMailboxChange
                  }
                  style={{
                    marginBottom: 18
                  }}
                >
                  {rows.map(
                    mailbox => (
                      <MenuItem
                        key={
                          mailbox.id
                        }
                        value={
                          mailbox.id
                        }
                      >
                        {mailbox.email}
                      </MenuItem>
                    )
                  )}
                </TextField>


                <Box
                  display="grid"
                  style={{
                    gap: 14
                  }}
                >
                  <TextField
                    fullWidth
                    variant="outlined"
                    size="small"
                    label="Nome"
                    placeholder="Ex.: Diego Nogueira"
                    value={
                      form.displayName
                    }
                    onChange={
                      updateField(
                        "displayName"
                      )
                    }
                  />


                  <TextField
                    fullWidth
                    variant="outlined"
                    size="small"
                    label="Cargo"
                    placeholder="Ex.: Diretor Comercial"
                    value={
                      form.jobTitle
                    }
                    onChange={
                      updateField(
                        "jobTitle"
                      )
                    }
                  />


                  <TextField
                    fullWidth
                    variant="outlined"
                    size="small"
                    label="Telefone / WhatsApp"
                    placeholder="Ex.: (11) 99999-9999"
                    value={
                      form.phone
                    }
                    onChange={
                      updateField(
                        "phone"
                      )
                    }
                  />


                  <TextField
                    fullWidth
                    variant="outlined"
                    size="small"
                    label="Empresa"
                    placeholder="Ex.: Pensou Seguros"
                    value={
                      form.companyName
                    }
                    onChange={
                      updateField(
                        "companyName"
                      )
                    }
                  />


                  <TextField
                    fullWidth
                    variant="outlined"
                    size="small"
                    label="Site"
                    placeholder="Ex.: pensouseguros.com.br"
                    value={
                      form.website
                    }
                    onChange={
                      updateField(
                        "website"
                      )
                    }
                  />


                  <Box
                    style={{
                      padding: 14,
                      border:
                        "1px solid #EAECF0",
                      borderRadius: 10,
                      background:
                        "#F9FAFB"
                    }}
                  >
                    <Typography
                      style={{
                        color:
                          "#344054",
                        fontSize: 11,
                        fontWeight: 800
                      }}
                    >
                      Logo da assinatura
                    </Typography>

                    <Typography
                      style={{
                        marginTop: 3,
                        color:
                          "#98A2B3",
                        fontSize: 9.5,
                        lineHeight: 1.5
                      }}
                    >
                      PNG, JPG ou WebP • máximo 1 MB
                    </Typography>


                    {form.logoData ? (
                      <Box
                        display="flex"
                        alignItems="center"
                        style={{
                          marginTop: 12,
                          gap: 12,
                          flexWrap:
                            "wrap"
                        }}
                      >
                        <Box
                          style={{
                            width: 92,
                            height: 58,
                            padding: 6,
                            border:
                              "1px solid #EAECF0",
                            borderRadius: 8,
                            background:
                              "#FFFFFF",
                            display:
                              "flex",
                            alignItems:
                              "center",
                            justifyContent:
                              "center"
                          }}
                        >
                          <img
                            src={`data:${form.logoContentType};base64,${form.logoData}`}
                            alt="Logo da assinatura"
                            style={{
                              display:
                                "block",
                              maxWidth:
                                "100%",
                              maxHeight:
                                "100%",
                              objectFit:
                                "contain"
                            }}
                          />
                        </Box>


                        <Box
                          style={{
                            display:
                              "flex",
                            gap: 8,
                            flexWrap:
                              "wrap"
                          }}
                        >
                          <Button
                            component="label"
                            variant="outlined"
                            size="small"
                            style={{
                              textTransform:
                                "none",
                              fontWeight: 700
                            }}
                          >
                            Trocar logo

                            <input
                              type="file"
                              hidden
                              accept="image/png,image/jpeg,image/webp"
                              onChange={
                                handleLogoChange
                              }
                            />
                          </Button>


                          <Button
                            size="small"
                            onClick={
                              handleRemoveLogo
                            }
                            style={{
                              textTransform:
                                "none",
                              fontWeight: 700,
                              color:
                                "#B42318"
                            }}
                          >
                            Remover
                          </Button>
                        </Box>
                      </Box>
                    ) : (
                      <Button
                        component="label"
                        variant="outlined"
                        size="small"
                        style={{
                          marginTop: 12,
                          textTransform:
                            "none",
                          fontWeight: 750
                        }}
                      >
                        Adicionar logo

                        <input
                          type="file"
                          hidden
                          accept="image/png,image/jpeg,image/webp"
                          onChange={
                            handleLogoChange
                          }
                        />
                      </Button>
                    )}
                  </Box>


                  <FormControlLabel
                    control={
                      <Switch
                        checked={
                          Boolean(
                            form.enabled
                          )
                        }
                        color="primary"
                        onChange={
                          event =>
                            setForm(
                              previous => ({
                                ...previous,

                                enabled:
                                  event
                                    .target
                                    .checked
                              })
                            )
                        }
                      />
                    }
                    label={
                      <Typography
                        style={{
                          color: "#344054",
                          fontSize: 11,
                          fontWeight: 700
                        }}
                      >
                        Usar esta assinatura
                      </Typography>
                    }
                  />
                </Box>
              </Box>


              {/* PREVIEW */}

              <Box
                style={{
                  padding: 24,
                  background: "#F9FAFB"
                }}
              >
                <Typography
                  style={{
                    color: "#667085",
                    fontSize: 9,
                    fontWeight: 850,
                    textTransform: "uppercase",
                    letterSpacing: ".07em"
                  }}
                >
                  Pré-visualização
                </Typography>


                <Paper
                  elevation={0}
                  style={{
                    marginTop: 12,
                    padding: 24,
                    borderRadius: 14,
                    border:
                      "1px solid #EAECF0",
                    background: "#FFFFFF"
                  }}
                >
                  <Typography
                    style={{
                      color: "#475467",
                      fontSize: 11,
                      lineHeight: 1.65
                    }}
                  >
                    Atenciosamente,
                  </Typography>


                  <Box
                    style={{
                      marginTop: 18,
                      paddingLeft: 14,
                      borderLeft:
                        "3px solid #0057FF"
                    }}
                  >
                    <Typography
                      style={{
                        color: "#101828",
                        fontSize: 15,
                        fontWeight: 900
                      }}
                    >
                      {form.displayName ||
                        "Seu nome"}
                    </Typography>


                    {(form.jobTitle ||
                      form.companyName) && (
                      <Typography
                        style={{
                          marginTop: 3,
                          color: "#475467",
                          fontSize: 10.5,
                          fontWeight: 650
                        }}
                      >
                        {[
                          form.jobTitle,
                          form.companyName
                        ]
                          .filter(Boolean)
                          .join(" • ")}
                      </Typography>
                    )}


                    {form.phone && (
                      <Typography
                        style={{
                          marginTop: 8,
                          color: "#667085",
                          fontSize: 10
                        }}
                      >
                        {form.phone}
                      </Typography>
                    )}


                    {selectedMailbox
                      ?.email && (
                      <Typography
                        style={{
                          marginTop: 3,
                          color: "#667085",
                          fontSize: 10
                        }}
                      >
                        {
                          selectedMailbox
                            .email
                        }
                      </Typography>
                    )}


                    {form.website && (
                      <Typography
                        style={{
                          marginTop: 3,
                          color: "#0057FF",
                          fontSize: 10,
                          fontWeight: 700
                        }}
                      >
                        {normalizeWebsite(
                          form.website
                        )}
                      </Typography>
                    )}
                    {form.logoData && (
                      <Box
                        style={{
                          marginTop: 14
                        }}
                      >
                        <img
                          src={`data:${form.logoContentType};base64,${form.logoData}`}
                          alt={
                            form.companyName ||
                            form.displayName ||
                            "Logo"
                          }
                          style={{
                            display:
                              "block",
                            maxWidth: 180,
                            maxHeight: 80,
                            width:
                              "auto",
                            height:
                              "auto",
                            objectFit:
                              "contain"
                          }}
                        />
                      </Box>
                    )}


                  </Box>
                </Paper>


                <Typography
                  style={{
                    marginTop: 12,
                    color: "#98A2B3",
                    fontSize: 9,
                    lineHeight: 1.55
                  }}
                >
                  A assinatura ativa será adicionada automaticamente a novos e-mails, respostas e encaminhamentos.
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>


        <Divider />


        <DialogActions
          style={{
            padding:
              "15px 24px",
            justifyContent:
              "space-between"
          }}
        >
          <Typography
            style={{
              color: "#98A2B3",
              fontSize: 9
            }}
          >
            Cada caixa pode ter sua própria assinatura.
          </Typography>


          <Box
            display="flex"
            style={{
              gap: 8
            }}
          >
            <Button
              disabled={
                saving
              }
              onClick={() =>
                setOpen(false)
              }
              style={{
                textTransform: "none",
                fontWeight: 750
              }}
            >
              Fechar
            </Button>


            <Button
              variant="contained"
              color="primary"
              disabled={
                saving ||
                !selectedMailboxId
              }
              onClick={
                handleSave
              }
              style={{
                minWidth: 145,
                height: 39,
                borderRadius: 9,
                textTransform: "none",
                fontWeight: 850,
                boxShadow: "none"
              }}
            >
              {saving ? (
                <CircularProgress
                  size={17}
                  color="inherit"
                />
              ) : (
                "Salvar assinatura"
              )}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </>
  );
};


export default EmailSignatureManager;
